/**
 * Aimepic Company Branding Configuration
 *
 * Branding for Aimepic as a software company (parent brand)
 * Separate from OneCare product branding
 */

export const COMPANY_BRANDING = {
  // Company Information
  company: {
    name: 'Aimepic',
    fullName: 'Aimepic',
    tagline: 'Intuitive Realities',
    description: 'Software company building secure, intelligent products for work and life — from healthcare operations to personal finance — that turn complexity into clarity and reliable execution.',
    shortDescription: 'Secure, intelligent software for work & life',
  },

  // Company Mission & Vision
  mission: {
    title: 'Our Mission',
    description: 'To empower organizations and individuals with intelligent, secure, and user-friendly software that brings clarity and control to complex, high-stakes parts of work and life.',
  },

  vision: {
    title: 'Our Vision',
    description: 'To be a trusted software partner for teams and individuals worldwide, delivering technology that makes demanding work and everyday life more organized, efficient, and effective.',
  },

  // Expertise Areas
  expertise: [
    {
      title: 'Healthcare Operations',
      description: 'Comprehensive platforms for managing hospital operations, staff coordination, and clinical workflows.',
      icon: 'hospital',
    },
    {
      title: 'Personal Finance',
      description: 'Tools that bring clarity and control to money — tracking, planning, and understanding personal finances.',
      icon: 'users',
    },
    {
      title: 'AI & Automation',
      description: 'Intelligent systems that leverage AI to assist professionals and automate routine tasks.',
      icon: 'sparkles',
    },
    {
      title: 'Security & Compliance',
      description: 'Enterprise-grade security and privacy-first, compliance-ready architecture — including HIPAA-ready healthcare solutions.',
      icon: 'shield',
    },
  ],

  // Value Propositions
  valueProps: [
    {
      title: 'Built for Real Life',
      description: 'Designed around real workflows and daily needs, by people who live the problems they solve.',
    },
    {
      title: 'Secure by Design',
      description: 'Privacy-first architecture with enterprise-grade security, protecting sensitive data at every level.',
    },
    {
      title: 'Innovation-Driven',
      description: 'Leveraging cutting-edge technology including AI, real-time communication, and modern cloud infrastructure.',
    },
    {
      title: 'Scalable Solutions',
      description: 'From individual users to large hospital systems, our software scales to meet your needs.',
    },
  ],

  // Contact Information
  contact: {
    email: 'contact-us@aimepic.com',
    salesEmail: 'contact-us@aimepic.com',
    supportEmail: 'contact-us@aimepic.com',
    website: 'https://aimepic.com',
    phone: '+1 (555) 123-4567',
  },

  // Portfolio Products
  portfolio: {
    title: 'Our Solutions',
    subtitle: 'A growing suite of secure, intelligent software products',
    products: [
      {
        id: 'onecare',
        name: 'OneCare Platform',
        tagline: 'Healthcare Platform',
        description: 'Comprehensive platform for healthcare operations, patient management, and clinical workflows. Streamline your healthcare facility with our unified solution.',
        link: '/onecare/',
        loginLink: '/onecare/login',
        features: [
          'Real-time staff communication',
          'Patient care coordination',
          'AI-powered assistance',
          'Clinical workflow management',
          'HIPAA-ready architecture',
        ],
        stats: [
          { label: 'Core Modules', value: '8+' },
          { label: 'Architecture', value: 'HIPAA-ready' },
          { label: 'Uptime', value: '99.9%' },
        ],
      },
    ],
  },

  // SEO & Meta
  meta: {
    defaultTitle: 'Aimepic - Secure, Intelligent Software for Work & Life',
    defaultDescription: 'Aimepic builds secure, intelligent software that turns complexity into clarity — from OneCare for healthcare operations to tools for personal finance and everyday life.',
    keywords: 'Aimepic, OneCare, secure software, healthcare technology, hospital management systems, personal finance software, AI-assisted workflows, productivity software',
  },

  // Copyright
  copyright: {
    year: new Date().getFullYear(),
    text: `© ${new Date().getFullYear()} Aimepic. All rights reserved.`,
  },
} as const;

// Export individual sections for convenience
export const { company, mission, vision, expertise, valueProps, contact, portfolio, meta, copyright } = COMPANY_BRANDING;

// Helper functions
export const getCompanyName = (): string => {
  return company.name;
};

export const getCompanyFullName = (): string => {
  return company.fullName;
};

export const getCompanyTagline = (): string => {
  return company.tagline;
};

// Default export
export default COMPANY_BRANDING;
